import * as React from 'react'
import { useForm } from 'react-hook-form'
import { Button, ErrorMessage, TextInput } from '../../../../components/UI'
import { emailPattern } from '../../../../lib/utils'
import { Styled } from './styled'
import { NewStyled as ParentStyled } from '../../styled'
import { useParams, useRegistrationContext } from '../../context/registration'
import { useTranslation } from '../../../../lib/context/i18n'
import { useLogin } from './hooks'
import { useSignupContext } from '../../context/signup'

type FieldValues = {
  email: string
  password: string
}

const getErrorText = (error: string) => {
  switch (error) {
    case 'pattern':
      return 'ERROR_IS_REQUIRED_EMAIL'

    case 'required':
      return 'ERROR_IS_REQUIRED'

    default:
      return 'ERROR_UNKNOWN'
  }
}

export const LoginForm = () => {
  const t = useTranslation()
  const { register, handleSubmit, formState } = useForm<FieldValues>({ mode: 'onBlur' })
  const { goto } = useRegistrationContext()
  const { loading, error, login } = useLogin()
  const {
    productName,
  } = useSignupContext()

  const onSubmit = React.useCallback(
    (data: FieldValues) => {
      login(data.email, data.password)
    },
    [login],
  )

  const { errors } = formState

  const handleForgotPassword = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      goto('/password-reset')
    },
    [goto, productName],
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ParentStyled.CardHeader>
        <ParentStyled.CardTitle>{t('LOGIN_TITLE')}</ParentStyled.CardTitle>
      </ParentStyled.CardHeader>
      <Styled.FieldWrapper>
        <div>
          <TextInput
            variant="dark"
            label={t('EMAIL')}
            type="email"
            required
            disabled={loading}
            {...register('email', { required: true, pattern: emailPattern })}
          />
          {errors.email && (
            <Styled.Block>
              <ErrorMessage $narrow $variant="light">
                {t(getErrorText(errors.email.type))}
              </ErrorMessage>
            </Styled.Block>
          )}
        </div>
        <div>
          <TextInput
            variant="dark"
            label={t('PASSWORD')}
            type="password"
            required
            disabled={loading}
            {...register('password', { required: true, min: 6 })}
          />
          {errors.password && (
            <Styled.Block>
              <ErrorMessage $narrow $variant="light">
                {t(getErrorText(errors.password.type))}
              </ErrorMessage>
            </Styled.Block>
          )}
        </div>
      </Styled.FieldWrapper>
      {error && (
        <Styled.Block>
          <ErrorMessage $variant="light">{t(error)}</ErrorMessage>
        </Styled.Block>
      )}
      <Styled.ForgotPasswordWrapper>
        <p>
          <a href="#" onClick={handleForgotPassword}>
            {t('FORGOT_PASSWORD')}
          </a>
        </p>
      </Styled.ForgotPasswordWrapper>
      <ParentStyled.CardFooter>
        <ParentStyled.CardFooterGroup>
          <Button type="submit" $inverted disabled={!formState.isValid || loading} iconRight="arrow-right">
            {t('LOGIN')}
          </Button>
          <Button
            type="button"
            $variant="secondary"
            disabled={loading}
            $inverted
            iconLeft="refresh"
            onClick={() => goto('/')}
          >
            {t('CHANGE_PRODUCT')}
          </Button>
        </ParentStyled.CardFooterGroup>
      </ParentStyled.CardFooter>
    </form>
  )
}
